/**
 * quoteTotals — o DINHEIRO do orçamento numa fonte única.
 *
 * POR QUE ISTO EXISTE: a mesma fórmula de "soma dos itens com desconto por
 * linha" estava copiada em TRÊS lugares — o cálculo inline do `QuoteForm`, a
 * função exportada `calculateTotal` do mesmo arquivo, e o `buildItemRows` dos
 * templates de PDF (mais uma quarta cópia parcial no `SortableQuoteItemCard` e
 * uma quinta, ERRADA, no `persistQuoteOffline`). Enquanto a conta era só "soma
 * os itens", a duplicação passava despercebida. Acrescentar frete e desconto de
 * cabeçalho a duas cópias e esquecer a terceira faria o PDF imprimir um total
 * diferente do que a tela mostra e do que o banco guardou — sem erro nenhum,
 * sem toast, sem log. Esse é o modo de falha central deste módulo.
 *
 * Aqui não há I/O nem React: função pura, testável, e é o que os guards usam.
 *
 * ─── ORDEM DE APLICAÇÃO (regra de NEGÓCIO, decidida e documentada) ───────────
 *
 *   1. `subtotal`          = Σ (quantidade × preço unitário) de cada item
 *   2. `descontoItens`     = Σ desconto de cada linha (valor ou %)
 *   3. `descontoCabecalho` = desconto do documento, aplicado sobre o subtotal
 *                            JÁ LÍQUIDO (`subtotal − descontoItens`)
 *   4. `frete`             = somado por ÚLTIMO, e NÃO recebe desconto
 *   5. `total`             = subtotal − descontoItens − descontoCabecalho + frete
 *
 * Por que o desconto de cabeçalho vem depois do de item: "10% no total" é o que
 * o prestador diz ao cliente olhando o valor final da proposta — o valor que ele
 * enxerga já está com os descontos de linha aplicados. Aplicar os dois sobre o
 * bruto daria um abatimento maior do que o combinado.
 *
 * Por que o frete NÃO recebe desconto: frete é despesa repassada, não margem. Um
 * desconto de 10% "no serviço" não pode corroer o que o prestador efetivamente
 * paga à transportadora — e um desconto em VALOR maior que os serviços zeraria a
 * entrega junto. É também o que a NF-e faz (desconto é por item; frete entra
 * depois, por fora). Consequência deliberada: o desconto de cabeçalho é limitado
 * ao subtotal líquido, então `total` nunca fica abaixo do frete, e nunca fica
 * negativo.
 *
 * ─── ARREDONDAMENTO (regra única, em centavos) ───────────────────────────────
 *
 * Todo valor exposto passa por {@link roundMoney} (centavos). O arredondamento
 * acontece POR LINHA antes de somar — não só no fim. É isso que garante que a
 * soma da tela nunca divirja do PDF por um centavo: o PDF imprime o total de
 * cada linha já arredondado, e o subtotal é a soma exatamente desses números
 * impressos. Arredondar só no fim faria a coluna do PDF somar diferente do
 * rodapé dele, que é o tipo de erro que o cliente encontra e o app não.
 */
import { roundMoney } from '@/lib/pricing';

export type QuoteDiscountType = 'value' | 'percent';

/** O mínimo que a conta precisa de um item — do formulário, do banco ou do cache. */
export interface QuoteTotalsItem {
  quantity?: number | string | null;
  unit_price?: number | string | null;
  discount?: number | string | null;
  discount_type?: QuoteDiscountType | string | null;
}

/** Os campos de cabeçalho que mexem no total (`quotes.shipping/discount/...`). */
export interface QuoteTotalsHeader {
  shipping?: number | string | null;
  discount?: number | string | null;
  discountType?: QuoteDiscountType | string | null;
}

export interface QuoteTotalsInput extends QuoteTotalsHeader {
  items?: readonly QuoteTotalsItem[] | null;
}

/** A decomposição inteira — nunca só o número final. */
export interface QuoteTotals {
  /** Σ (quantidade × preço), antes de qualquer desconto. */
  subtotal: number;
  /** Quanto os descontos POR ITEM abateram. */
  descontoItens: number;
  /** Quanto o desconto do DOCUMENTO abateu (já resolvido de % para R$). */
  descontoCabecalho: number;
  /** Frete, somado por último e livre de desconto. */
  frete: number;
  /** O que o cliente paga. É isto que vai para `quotes.total`. */
  total: number;
}

/**
 * Número tolerante à ORIGEM, mas ESTRITO quanto ao formato.
 *
 * As três origens desta função têm contratos diferentes: o formulário manda
 * `number`, o PostgREST manda `numeric` como string com PONTO decimal
 * ("250.50"), e a fila offline manda o que estiver no JSON serializado — que
 * pode ter sido montado por um bundle antigo, por um import, ou à mão.
 *
 * POR QUE O `Number(raw)` CRU NÃO SERVIA (dois modos de falha opostos):
 *
 *   - `Number('1,5')` é `NaN` → virava **0**, e a linha ficava DE GRAÇA. O PDF
 *     imprimia "1,5 × R$ 100,00 = R$ 0,00" e o prestador cobrava zero. Sem erro,
 *     sem toast, sem log. Qualquer caminho que entregasse a quantidade em pt-BR
 *     (cache antigo, import, fila de bundle anterior) caía nisso.
 *   - Na outra ponta, `Number` é permissivo DEMAIS: `'0x10'` vira 16, `'1e3'`
 *     vira 1000, `true` vira 1 e `[5]` vira 5. Nada disso é entrada legítima de
 *     um campo de dinheiro, e todos atravessavam silenciosos.
 *
 * A regra: a VÍRGULA decide. Se a string tem vírgula, ela é o separador decimal
 * (pt-BR) e os pontos são milhar. Se não tem, a string fica como está — que é
 * exatamente o formato que o PostgREST manda. Isso resolve a ambiguidade de
 * "1.234" sem chute: vindo do banco é mil e duzentos e trinta e quatro
 * milésimos, e é assim que continua sendo lido.
 *
 * Depois da normalização, só um decimal simples passa. Qualquer outra coisa é 0.
 */
const DECIMAL_SIMPLES = /^-?\d+(\.\d+)?$/;

function money(raw: unknown): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (typeof raw !== 'string') return 0;

  const text = raw.trim();
  if (!text) return 0;

  const normalized = text.includes(',')
    ? text.replace(/\./g, '').replace(',', '.')
    : text;

  if (!DECIMAL_SIMPLES.test(normalized)) return 0;

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Desconto de uma base, em valor ou percentual, nunca maior que a base nem
 * negativo. Mesma regra nos dois níveis (item e cabeçalho) de propósito — um
 * desconto de R$ 999 num item de R$ 50 abate R$ 50, não R$ 999.
 */
function discountAmount(
  base: number,
  rawDiscount: unknown,
  type: QuoteDiscountType | string | null | undefined,
): number {
  const value = money(rawDiscount);
  if (!(value > 0) || !(base > 0)) return 0;
  const amount = type === 'percent' ? (base * value) / 100 : value;
  return roundMoney(Math.min(Math.max(0, amount), base));
}

/**
 * Bruto de uma linha: quantidade × preço, em centavos.
 *
 * Quantidade e preço são CLAMPADOS em zero. Não é paranoia — é o conserto de um
 * furo real: uma linha com quantidade negativa produzia um bruto negativo que
 * ABATIA o valor das outras linhas. R$ 1.000 em serviços mais uma linha de
 * `-5 × R$ 100` gravava `quotes.total = 500`.
 *
 * O que tornava isso invisível: um desconto legítimo passa por
 * `discountAmount`, que o limita à base e o expõe em `descontoItens` (a tela, o
 * PDF e o relatório de margem mostram). Um bruto negativo **escapa de todos
 * esses limites** — vira um abatimento de 50% que não aparece como desconto em
 * lugar nenhum. E `quote_items.quantity` é `numeric` SEM CHECK, então um payload
 * forjado na fila offline chega assim ao Postgres.
 *
 * Clampar aqui (e não só na validação do formulário) é o que fecha os caminhos
 * que não passam por formulário: cache, import e fila offline.
 */
export function quoteItemGross(item: QuoteTotalsItem): number {
  const quantity = Math.max(0, money(item?.quantity));
  const unitPrice = Math.max(0, money(item?.unit_price));
  return roundMoney(quantity * unitPrice);
}

/**
 * Total de uma linha, com o desconto do item aplicado. Nunca negativo.
 *
 * É o valor gravado em `quote_items.total` e impresso na coluna TOTAL do PDF —
 * por isso já sai arredondado em centavos (ver a nota de arredondamento no topo).
 */
export function quoteItemLineTotal(item: QuoteTotalsItem): number {
  const gross = quoteItemGross(item);
  return roundMoney(gross - discountAmount(gross, item?.discount, item?.discount_type));
}

/**
 * A conta inteira, na ordem documentada no topo do arquivo.
 *
 * Único consumidor legítimo do resultado: quem exibe (tela/PDF/página pública) e
 * quem grava (`quotes.total`). Nenhum outro lugar deve refazer esta soma.
 */
export function computeQuoteTotals(input: QuoteTotalsInput): QuoteTotals {
  const items = input?.items ?? [];

  let subtotal = 0;
  let descontoItens = 0;
  for (const item of items) {
    const gross = quoteItemGross(item);
    subtotal = roundMoney(subtotal + gross);
    descontoItens = roundMoney(descontoItens + (gross - quoteItemLineTotal(item)));
  }

  // Base do desconto de cabeçalho: o subtotal JÁ LÍQUIDO dos descontos de item.
  const liquido = roundMoney(subtotal - descontoItens);
  const descontoCabecalho = discountAmount(liquido, input?.discount, input?.discountType);

  // Frete entra depois e não é descontado — ver a justificativa no topo.
  const frete = roundMoney(Math.max(0, money(input?.shipping)));

  const total = roundMoney(Math.max(0, liquido - descontoCabecalho + frete));

  return { subtotal, descontoItens, descontoCabecalho, frete, total };
}

/**
 * Há algo a DECOMPOR? Um orçamento simples (sem desconto nenhum e sem frete)
 * mostra só a linha de Total — na tela, na página pública e no PDF. A regra vive
 * aqui porque os três precisam concordar: se um deles decidisse sozinho, o
 * cliente veria "Subtotal" no PDF e não na tela (ou o contrário).
 */
export function hasTotalsBreakdown(totals: QuoteTotals): boolean {
  return totals.descontoItens > 0 || totals.descontoCabecalho > 0 || totals.frete > 0;
}

/** Atalho para quem só precisa do número que vai para `quotes.total`. */
export function quoteGrandTotal(input: QuoteTotalsInput): number {
  return computeQuoteTotals(input).total;
}

/**
 * Cabeçalho normalizado a partir de uma linha de `quotes` (servidor, cache ou
 * fila offline), onde os campos vêm em snake_case e o `numeric` pode chegar como
 * string. Existe para que ninguém precise lembrar de ler `discount_type` na mão
 * — esquecer um dos três campos faz o total recalculado divergir em silêncio.
 */
export function quoteHeaderFromRow(row: {
  shipping?: number | string | null;
  discount?: number | string | null;
  discount_type?: string | null;
} | null | undefined): { shipping: number; discount: number; discountType: QuoteDiscountType } {
  return {
    shipping: money(row?.shipping),
    discount: money(row?.discount),
    discountType: row?.discount_type === 'percent' ? 'percent' : 'value',
  };
}

/**
 * Os três campos de cabeçalho prontos para um payload de `quotes`.
 *
 * Toda gravação passa por aqui em vez de repetir as três linhas: os payloads de
 * `quotes` são montados por LISTA EXPLÍCITA de colunas em ~10 lugares
 * (armadilha #33/#62), e um save que esqueça `shipping` grava com sucesso, sem
 * erro de RLS, e some com o frete.
 */
export function buildQuoteTotalsColumns(header: QuoteTotalsHeader & { items?: readonly QuoteTotalsItem[] | null }): {
  shipping: number;
  discount: number;
  discount_type: QuoteDiscountType;
  total: number;
} {
  const totals = computeQuoteTotals(header);
  return {
    shipping: totals.frete,
    discount: roundMoney(Math.max(0, money(header?.discount))),
    discount_type: header?.discountType === 'percent' ? 'percent' : 'value',
    total: totals.total,
  };
}
